import {
  getProduct,
  getProductPricingModel,
  listItemPrices,
  listProductBundleItems,
  type BundleItem,
  type Product,
} from "./catalog";

type OperationalProfile = {
  bundle_items?: BundleItem[];
  bundle_base_price?: { value?: number } | null;
};

type ItemPriceRow = { price_type?: string; unit_price?: number; is_active?: boolean };

function priceFromModelAndItems(
  pm: { base_rate?: number } | null,
  itemPrices: ItemPriceRow[]
): number {
  let baseRate = typeof pm?.base_rate === "number" ? pm.base_rate : 0;
  if (baseRate > 0 || itemPrices.length === 0) return baseRate;

  const activePrices = itemPrices.filter((p) => p.is_active !== false);
  const publicPrice = activePrices.find(
    (p) => p.price_type === "public" && (p.unit_price ?? 0) > 0
  );
  const firstPositive = activePrices.find((p) => (p.unit_price ?? 0) > 0);
  const first = publicPrice ?? firstPositive ?? activePrices[0] ?? itemPrices[0];
  return typeof first?.unit_price === "number" ? first.unit_price : 0;
}

async function loadBundleItems(productId: string, product: Product | null): Promise<BundleItem[]> {
  const profile = (product as Product & { operational_cost_profile?: OperationalProfile })
    ?.operational_cost_profile;
  const fromProfile = profile?.bundle_items;
  if (Array.isArray(fromProfile) && fromProfile.length > 0) return fromProfile;

  const fromTable = await listProductBundleItems(productId).catch(() => []);
  return fromTable.map((bi) => ({
    product_id: bi.component_product_id,
    quantity: bi.qty_used ?? bi.quantity ?? 1,
    required: bi.is_required,
  }));
}

/** Prix unitaire d'un produit ; pour un bundle sans prix propre, somme récursive des composants. */
export async function resolveProductUnitPrice(
  productId: string,
  cache: Map<string, number>,
  visited: Set<string> = new Set()
): Promise<number> {
  if (cache.has(productId)) return cache.get(productId)!;
  if (visited.has(productId)) return 0;
  visited.add(productId);

  const [pm, itemPrices, product] = await Promise.all([
    getProductPricingModel(productId).catch(() => null),
    listItemPrices(productId).catch(() => []),
    getProduct(productId).catch(() => null),
  ]);

  const direct = priceFromModelAndItems(pm, itemPrices);
  if (direct > 0) {
    cache.set(productId, direct);
    return direct;
  }

  if (!product || product.product_type !== "bundle") {
    cache.set(productId, 0);
    return 0;
  }

  const bundleItems = await loadBundleItems(productId, product);
  const profile = (product as Product & { operational_cost_profile?: OperationalProfile })
    .operational_cost_profile;
  const baseAmount =
    profile?.bundle_base_price && typeof profile.bundle_base_price.value === "number"
      ? profile.bundle_base_price.value
      : 0;

  let subtotal = baseAmount;
  for (const item of bundleItems) {
    if (item.required === false) continue;
    const qty = typeof item.quantity === "number" && item.quantity >= 0.01 ? item.quantity : 1;
    const unit = await resolveProductUnitPrice(item.product_id, cache, visited);
    subtotal += unit * qty;
  }

  cache.set(productId, subtotal);
  return subtotal;
}

export async function resolveBundleComponentPricing(
  productIds: string[]
): Promise<Record<string, { mode: string; base_rate: number }>> {
  const unique = [...new Set(productIds)];
  const cache = new Map<string, number>();
  const entries = await Promise.all(
    unique.map(async (pid) => {
      const [pm, baseRate] = await Promise.all([
        getProductPricingModel(pid).catch(() => null),
        resolveProductUnitPrice(pid, cache),
      ]);
      return [pid, { mode: pm?.mode ?? "fixed", base_rate: baseRate }] as const;
    })
  );
  return Object.fromEntries(entries);
}
